import { BillOfMaterialsStatus } from "@redotech/cockroachdb-codegen/tables/inventory/bill-of-materials-status";
import { billOfMaterialsIdFormat } from "@redotech/redo-fulfillment-id";
import { BomProductConditions } from "@redotech/redo-inventory-common/bill-of-materials/bom-product-condition";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const billOfMaterialsStatusSchema = createEnum(BillOfMaterialsStatus);

/** Statuses that participate in capacity planning (work orders, forecasting). */
export const billOfMaterialsActiveStatusSchema = createEnum([
  BillOfMaterialsStatus.Active,
  BillOfMaterialsStatus.Draft,
]);

export const billOfMaterialsStatusDisplayNames: Record<
  BillOfMaterialsStatus,
  string
> = {
  [BillOfMaterialsStatus.Active]: "Active",
  [BillOfMaterialsStatus.Draft]: "Internal only",
  [BillOfMaterialsStatus.Inactive]: "Inactive",
};

export const BillOfMaterialsProductKind = createEnum(["input", "output"]);
export type BillOfMaterialsProductKind = z.infer<
  typeof BillOfMaterialsProductKind
>;

const BillOfMaterialsProductInput_ = z.object({
  productId: productIdFormat.schema,
  kind: BillOfMaterialsProductKind,
  quantity: z.number().int().positive(),
  position: z.number().int().min(0),
  conditions: BomProductConditions.optional(),
});
export const BillOfMaterialsProductInput: z.ZodType<BillOfMaterialsProductInput> =
  BillOfMaterialsProductInput_;
export interface BillOfMaterialsProductInput extends z.infer<
  typeof BillOfMaterialsProductInput_
> {}

const BillOfMaterialsProduct_ = z.object({
  productId: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema.nullable(),
  productTitle: z.string(),
  productSku: z.string().nullable(),
  productImageUrl: z.string().nullable(),
  kind: BillOfMaterialsProductKind,
  quantity: z.number().int(),
  position: z.number().int(),
  conditions: BomProductConditions.optional(),
});
export const BillOfMaterialsProduct: z.ZodType<BillOfMaterialsProduct> =
  BillOfMaterialsProduct_;
export interface BillOfMaterialsProduct extends z.infer<
  typeof BillOfMaterialsProduct_
> {}

const BillOfMaterials_ = z.object({
  id: billOfMaterialsIdFormat.schema,
  name: z.string(),
  tags: z.array(z.string()),
  status: billOfMaterialsStatusSchema,
  buildable: z.number().int(),
  products: z.array(BillOfMaterialsProduct),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const BillOfMaterials: z.ZodType<BillOfMaterials> = BillOfMaterials_;
export interface BillOfMaterials extends z.infer<typeof BillOfMaterials_> {}

const BillOfMaterialsListItem_ = z.object({
  id: billOfMaterialsIdFormat.schema,
  name: z.string(),
  tags: z.array(z.string()),
  status: billOfMaterialsStatusSchema,
  buildable: z.number().int(),
  inputCount: z.number().int(),
  outputCount: z.number().int(),
  /** Total inventory for output products (sum of location_inventory.quantity) */
  outputInventory: z.number().int().nullable(),
  inputProductImages: z.array(z.string()),
  outputProductImages: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const BillOfMaterialsListItem: z.ZodType<BillOfMaterialsListItem> =
  BillOfMaterialsListItem_;
export interface BillOfMaterialsListItem extends z.infer<
  typeof BillOfMaterialsListItem_
> {}
